import {useEffect, useState} from "react";
import {Textarea} from "@/components/ui/textarea";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {Avatar} from "@/components/ui/avatar";
import {Card} from "@/components/ui/card";
import {Badge} from "@/components/ui/badge";
import {MessageCircle, Send, Award, ThumbsUp} from "lucide-react";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/components/ui/tooltip";
import {HoverCard, HoverCardContent, HoverCardTrigger} from "@/components/ui/hover-card";
import {useQuery} from "@tanstack/react-query";
import {Poll, PollStatus} from "@/types/Poll.ts";
import {Session} from "@supabase/supabase-js";
import {FaCircleExclamation} from "react-icons/fa6";
import {toast} from "sonner";
import {useAuth} from "@/contexts/AuthContext.tsx";


enum PollCommentStatus {
    pending = 0,
    active = 1,
    inactive = 2,
    deleted = 3,
}

interface PollComment {
    id: string,
    pollId: string,
    userId: string,
    comment: string,
    createdAt: Date,
    replies?: PollComment[],
    users?: { username: string, badge?: { name: string, color: string, }, selectedOption?: string },
    likeCount?: number,
    status?: PollCommentStatus,
    parentId?: string | null,
}
interface PollCommentResponse{
    data: PollComment[];
    pages: number;
}
export const Comments = ({pollId}) => {
    const [commentText, setCommentText] = useState("");
    const [replyingTo, setReplyingTo] = useState<string | null>(null);
    const [replyText, setReplyText] = useState("");
    const [userLikes, setUserLikes] = useState<Record<string, boolean>>({});
    const MAX_CHARS = 750;
    const [currentPage, setCurrentPage] = useState<number | null>(1);
    const itemsPerPage = 10;
    const [allComments, setAllComments] = useState([]);
    /*const [userSession] = useState<Session | null>(() => {
        const cachedSession = localStorage.getItem('session');
        return cachedSession ? JSON.parse(cachedSession) : null;
    });*/
    const {user, session, loading} = useAuth();
    const {data: comments, isLoading, refetch} = useQuery({
        queryKey: ['comments',session],
        queryFn: async () => {
            const url = `${import.meta.env.VITE_API_URL}/polls/comments/${pollId}?page=${currentPage}&pageSize=${itemsPerPage}`;
            let headers = {}
            if(session){
                 headers = {
                    'Authorization': `Bearer ${session.access_token}`
                };

            }
            const response = await fetch(url, {
                method: "GET",
                headers
            });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data: PollCommentResponse = await response.json();
            // const activeComments = data.filter(comment => comment.status == PollCommentStatus.active);
            // console.log(activeComments);
            setAllComments(data.data);
            return data;
        }
    });

    const {data: commentUserLikes} = useQuery({
        queryKey: ['commentUserLikes',session],
        queryFn: async () => {
            const url = `${import.meta.env.VITE_API_URL}/polls/comments/${pollId}/likes`
            const headers = {
                'Authorization': `Bearer ${session.access_token}`
            };
            const response = await fetch(url, {
                method: "GET",
                headers
            });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            data.map((commentLike) => {
                setUserLikes(prev => {
                    const key = commentLike.comment_id;
                    const newLikes = {...prev};
                    newLikes[key] = !newLikes[key];
                    return newLikes;
                });
            })

            // const activeComments = data.filter(comment => comment.status == PollCommentStatus.active);
            // console.log(activeComments);
            // setAllComments(data);

            return data;
        },
        enabled: !!session
    });

    useEffect(() => {
        if (commentUserLikes && commentUserLikes.length > 0) {
            commentUserLikes.map((likes) => {
                const key = `${likes.commentId}`;
                // const key = `${type}-${id}`;
                if (key)
                    setUserLikes(prev => {
                        const newLikes = {...prev};
                        newLikes[key] = !newLikes[key];
                        return newLikes;
                    });
            })
        }
    }, [commentUserLikes]);
    const sendReply = async (comment: string) => {
        try {
            const url = `${import.meta.env.VITE_API_URL}/polls/comments/${pollId}`;
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`
            };
            const body = JSON.stringify({comment: comment, parentId: replyingTo});
            const response = await fetch(url, {
                method: 'POST',
                headers,
                body,
            });

            const responseData = await response.json();
            // console.log("responseData:", responseData)
            setCommentText("");
            setReplyText("");
            setReplyingTo(null);
            refetch();
            if (responseData.data) {

                return {success: true, message: null}
            } else {
                return {success: false, message: responseData.error}
            }
        } catch (error) {
            // console.error('Error:', error);
            return {success: false, message: error.message}
        }
    }

    const toggleCommentLike = async (commentId: string) => {
        // console.log("this");
        try {
            const url = `${import.meta.env.VITE_API_URL}/polls/comments/${pollId}/likes/${commentId}`;
            const headers = {
                'Authorization': `Bearer ${session.access_token}`
            };
            const response = await fetch(url, {
                method: 'POST',
                headers,
            });

            const responseData = await response.json();
            // console.log("toggleCommentLike:", responseData)

            const key = `${responseData.commentId}`;
            // const key = `${type}-${id}`;
            setUserLikes(prev => {
                const newLikes = {...prev};
                newLikes[key] = !newLikes[key];
                return newLikes;
            });
            /*setAllComments(allComments.map(comment =>
                comment.id === responseData.commentId
                    ? { ...comment, likeCount: responseData.count }
                    : comment
            ));*/
            setAllComments(allComments.map(comment =>
                comment.id === responseData.commentId
                    ? {...comment, likeCount: responseData.count}
                    : {
                        ...comment,
                        replies: comment.replies.map(reply =>
                            reply.id === responseData.commentId
                                ? {...reply, likeCount: responseData.count}
                                : reply
                        )
                    }
            ));

            if (responseData.data) {
                return {success: true, message: null}
            } else {
                return {success: false, message: responseData.error}
            }
        } catch (error) {
            // console.error('Error:', error);
            return {success: false, message: error.message}
        }
    }

    const handleCommentSubmit = async () => {
        if (commentText.trim() === "") return;
        // console.log("Submitting comment:", commentText);
        if(session) {
            await sendReply(commentText);
        } else{
            return toast.error("You must login to comment on this poll!")
        }
        // In a real implementation, this would send the comment to a database
    };

    const handleReplySubmit = async (commentId: string) => {
        if (replyText.trim() === "") return;
        // console.log("Submitting reply to comment", commentId, ":", replyText);
        if(session) {
            await sendReply(replyText);
        } else{
            return toast.error("You must login to reply!")
        }

        // In a real implementation, this would send the reply to a database
    };

    const toggleReplyForm = async (commentId: string) => {
        if(!session){
            return toast.error("You must login to reply!")
        }
        setReplyingTo(replyingTo === commentId ? null : commentId);
        setReplyText("");
    };

    const toggleLike = async (id: string, type: 'comment' | 'reply') => {
        if(!session){
            return toast.error("You must login to like a comment or reply!")
        }
        // console.log("id:", id);
        await toggleCommentLike(id);
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Function to get the like count based on original count and user action
    const getLikeCount = (originalCount: number = 0, id: string, type: 'comment' | 'reply') => {
        // const key = `${type}-${id}`;
        const key = `${id}`;
        /*if (userLikes[key]) {
            return originalCount + 1;
        }*/
        return originalCount;
    };

    return (
        <Card className="p-6 mb-8 dark:bg-gray-800/50 dark:border-gray-700">
            <h2 className="text-xl font-semibold mb-6 dark:text-white">What Does the Community Think?</h2>

            {/* Comment Form */}
            {/*{userSession?.access_token && (*/}
            <div className="mb-8">
                <Textarea
                    placeholder="Add your comment (max 750 characters)..."
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value.slice(0, MAX_CHARS))}
                    className="min-h-[100px] mb-2 resize-none dark:bg-gray-700/50 dark:border-gray-600 dark:text-gray-100"
                />
                <div className="flex justify-between items-center">
                      <span
                          className={`text-sm ${commentText.length >= MAX_CHARS ? 'text-red-500' : 'text-gray-500'} dark:text-gray-400`}>
                        {commentText.length}/{MAX_CHARS} characters
                      </span>
                    <Button
                        onClick={handleCommentSubmit}
                        className="bg-[#96A58D] hover:bg-[#96A58D]/90 text-white dark:bg-[#2C3930] dark:hover:bg-[#2C3930]/90"
                        disabled={commentText.trim() === ""}
                    >
                        <Send className="h-4 w-4 mr-2"/>
                        Send
                    </Button>
                </div>
            </div>
            {/*)}*/}


            {/* Comments List */}
            <div className="space-y-6">
                {(allComments && allComments.length > 0) ? allComments?.map((comment) => (
                    <div key={comment.id} className="border-b border-gray-200 dark:border-gray-700 pb-6 last:border-0">
                        {/* Comment */}
                        <div className="flex gap-4">
                            <Avatar className="h-10 w-10 bg-gray-200 dark:bg-gray-700">
                                <div
                                    className="flex items-center justify-center h-full w-full text-gray-500 dark:text-gray-300 font-semibold">
                                    {`${comment.users.username.charAt(0).toUpperCase()}`}
                                </div>
                            </Avatar>
                            <div className="flex-1">
                                <div className="flex items-center flex-wrap gap-2 mb-1">
                                    {comment.users.badge && (
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <div
                                                        className={`inline-flex items-center ${comment.users.badge.color} text-white rounded-full p-1 mr-1`}>
                                                        <Award className="h-3 w-3"/>
                                                    </div>
                                                </TooltipTrigger>
                                                <TooltipContent side="top">
                                                    <p>{comment.users.badge.name} Badge</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    )}
                                    {/*- <span className="bg-[#96A58D] dark:bg-[#2C3930] text-white px-2">Voted for {comment.users.userVote}</span>*/}
                                    <span className="font-semibold dark:text-white">{comment.users.username}</span>
                                    {comment.users.userVote ? (
                                        <Badge variant="outline"
                                               className="ml-2 bg-gray-100 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300">
                                            Voted: {comment.users.userVote}
                                        </Badge>
                                    ) : (
                                        <Badge variant="outline"
                                               className="ml-2 bg-red-500 dark:bg-red-500/50 text-gray-700 dark:text-gray-300">
                                            Not voted
                                        </Badge>
                                    )}
                                </div>
                                <p className="text-gray-700 dark:text-gray-300 mb-2">{comment.comment}</p>
                                <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 gap-4">
                                    <span>{formatDate((new Date(comment.createdAt)).toISOString())}</span>

                                    {/* Like Button */}
                                    <button
                                        onClick={() => toggleLike(comment.id, 'comment')}
                                        className={`flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200 ${userLikes[`${comment.id}`] ? 'text-green-500 dark:text-green-400' : ''}`}
                                    >
                                        <ThumbsUp className="h-4 w-4"/>
                                        <span>{getLikeCount(comment.likeCount, comment.id, 'comment')}</span>
                                    </button>

                                    <button
                                        onClick={() => toggleReplyForm(comment.id)}
                                        className="flex items-center hover:text-gray-700 dark:hover:text-gray-200"
                                    >
                                        <MessageCircle className="h-4 w-4 mr-1"/>
                                        Reply
                                    </button>
                                </div>

                                {/* Reply Form */}
                                {replyingTo === comment.id && (
                                    <div className="mt-4">
                                        <Input
                                            placeholder="Write a reply..."
                                            value={replyText}
                                            onChange={(e) => setReplyText(e.target.value.slice(0, MAX_CHARS))}
                                            className="mb-2 dark:bg-gray-700/50 dark:border-gray-600 dark:text-gray-100"
                                        />
                                        <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {replyText.length}/{MAX_CHARS} characters
                      </span>
                                            <div className="space-x-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setReplyingTo(null)}
                                                    className="dark:border-gray-600 dark:text-gray-300"
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    onClick={() => handleReplySubmit(comment.id)}
                                                    className="bg-[#96A58D] hover:bg-[#96A58D]/90 text-white dark:bg-[#2C3930] dark:hover:bg-[#2C3930]/90"
                                                    disabled={replyText.trim() === ""}
                                                >
                                                    Reply
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Replies */}
                                {comment?.replies?.length > 0 && (
                                    <div
                                        className="mt-4 pl-4 border-l-2 border-gray-200 dark:border-gray-700 space-y-4">
                                        {comment?.replies?.map((reply) => (
                                            <div key={reply.id} className="flex gap-3">
                                                <Avatar className="h-8 w-8 bg-gray-200 dark:bg-gray-700">
                                                    <div
                                                        className="flex items-center justify-center h-full w-full text-gray-500 dark:text-gray-300 font-semibold text-xs">
                                                        {reply.users.username.charAt(0).toUpperCase()}
                                                    </div>
                                                </Avatar>
                                                <div>
                                                    <div className="flex items-center flex-wrap gap-2 mb-1">
                                                        {reply.users.badge && (
                                                            <TooltipProvider>
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <div
                                                                            className={`inline-flex items-center ${reply.users.badge.color} text-white rounded-full p-1 mr-1`}>
                                                                            <Award className="h-3 w-3"/>
                                                                        </div>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent side="top">
                                                                        <p>{reply.users.badge.name} Badge</p>
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            </TooltipProvider>
                                                        )}
                                                        {/*- {reply.users.uservote ? (<span className="bg-[#96A58D] dark:bg-[#2C3930] text-white px-2">Voted for {reply.users.userVote}</span>) : () }*/}
                                                        <span
                                                            className="font-semibold dark:text-white">{reply.users.username} </span>
                                                        {reply.users.userVote ? (
                                                            <Badge variant="outline"
                                                                   className="ml-2 bg-gray-100 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 text-xs">
                                                                Voted: {reply.users.userVote}
                                                            </Badge>
                                                        ) : (
                                                            <Badge variant="outline"
                                                                   className="ml-2 bg-red-500 dark:bg-red-500/50 text-white dark:text-white">
                                                                Not voted
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-gray-700 dark:text-gray-300 text-sm">{reply.comment}</p>
                                                    <div
                                                        className="flex items-center text-xs text-gray-500 dark:text-gray-400 mt-1 gap-3">
                                                        <span>{formatDate((new Date(reply.createdAt)).toISOString())}</span>

                                                        {/* Like Button for Replies */}
                                                        <button
                                                            onClick={() => toggleLike(reply.id, 'reply')}
                                                            className={`flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200 ${userLikes[`${reply.id}`] ? 'text-green-500 dark:text-green-400' : ''}`}
                                                        >
                                                            <ThumbsUp className="h-3 w-3"/>
                                                            <span>{getLikeCount(reply.likeCount, reply.id, 'reply')}</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>
                )) : (
                    <div className="flex items-center">
                        <FaCircleExclamation className="h-6 w-6"/>
                        <span className="text-lg ml-2">No comments yet!</span>
                    </div>
                )}
            </div>
            <div className="flex justify-center gap-2 mt-4">
                <Button
                    variant="outline"
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="dark:border-gray-600 dark:text-gray-200 dark:hover:bg-[#2C3930]"
                >
                    Previous
                </Button>
                <span className="py-2 px-4 dark:text-gray-300">
                                                {/*Page {currentPage} of {totalPages}*/}
                    Page {currentPage} of {comments?.pages}
                                            </span>
                <Button
                    variant="outline"
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, comments?.pages))}
                    // disabled={currentPage === totalPages}
                    disabled={currentPage === comments?.pages}
                    className="dark:border-gray-600 dark:text-gray-200 dark:hover:bg-[#2C3930]"
                >
                    Next
                </Button>
            </div>
        </Card>
    );
};
